import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { USER_ROLES } from '../../utils/constants';

const Navbar = () => {
  const { user, isAuthenticated, hasRole, logout } = useAuth();
  const { dark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = hasRole([USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]);

  // Nav classes based on theme
  const navBg = dark
    ? scrolled
      ? 'bg-black/95 backdrop-blur-xl shadow-2xl shadow-red-600/10 border-red-600/30'
      : 'bg-black/80 backdrop-blur-md border-red-600/20'
    : scrolled
      ? 'bg-white/95 backdrop-blur-xl shadow-lg shadow-gray-200/80 border-gray-200'
      : 'bg-white/85 backdrop-blur-md border-gray-100';

  const linkClass = dark
    ? 'text-gray-300 hover:text-white'
    : 'text-gray-600 hover:text-gray-900';

  return (
    <nav className={`fixed w-full top-0 z-50 transition-all duration-500 border-b ${navBg} `}>
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-600 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">

          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <div className={`p-2 rounded-xl border-2 transition-all duration-300 group-hover:shadow-lg group-hover:shadow-red-600/30 group-hover:border-red-600 ${dark ? 'bg-gradient-to-br from-white to-gray-100 border-gray-300' : 'bg-white border-gray-200'}`}>
                <img
                  src="/bb.png"
                  alt="eBallotBox Platform Logo"
                  className="h-10 w-auto transform group-hover:scale-105 transition-all duration-500"
                />
              </div>
            </Link>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex md:items-center md:space-x-1">
            <Link to="/" className={`relative px-5 py-2 rounded-lg text-sm font-medium transition-all duration-300 group ${linkClass}`}>
              <span className="relative z-10">Home</span>
              <span className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/20 to-red-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" />
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-red-600 group-hover:w-3/4 transition-all duration-300" />
            </Link>

            <Link to="/contests" className={`relative px-5 py-2 rounded-lg text-sm font-medium transition-all duration-300 group ${linkClass}`}>
              <span className="relative z-10">Contests</span>
              <span className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/20 to-red-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" />
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-red-600 group-hover:w-3/4 transition-all duration-300" />
            </Link>

            {isAuthenticated() ? (
              <>
                {isAdmin && (
                  <Link to="/admin/dashboard" className={`relative px-5 py-2 rounded-lg text-sm font-medium transition-all duration-300 group ${linkClass}`}>
                    <span className="relative z-10">Dashboard</span>
                    <span className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/20 to-red-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" />
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-red-600 group-hover:w-3/4 transition-all duration-300" />
                  </Link>
                )}

                {/* Profile Dropdown */}
                <div className="relative ml-3">
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 border ${dark ? 'text-gray-300 hover:text-white hover:bg-red-600/10 border-transparent hover:border-red-600/30' : 'text-gray-600 hover:text-gray-900 hover:bg-red-50 border-transparent hover:border-red-200'}`}
                  >
                    <div className="relative">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center text-white font-semibold shadow-lg ring-2 ring-red-600/50">
                        {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                      </div>
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-black" />
                    </div>
                    <span className={`font-medium hidden lg:block ${dark ? 'text-white' : 'text-gray-800'}`}>{user?.firstName}</span>
                    <svg className={`w-4 h-4 transition-transform duration-300 ${isProfileOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isProfileOpen && (
                    <div className={`absolute right-0 mt-3 w-64 border rounded-xl shadow-2xl py-2 z-50 transform origin-top animate-dropdown overflow-hidden ${dark ? 'bg-gray-900 border-red-600/30' : 'bg-white border-gray-200'}`}>
                      <div className={`absolute inset-0 bg-gradient-to-b from-red-600/5 to-transparent pointer-events-none`} />
                      <div className={`px-4 py-3 border-b relative ${dark ? 'border-gray-800' : 'border-gray-100'}`}>
                        <p className={`text-sm font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>{user?.firstName} {user?.lastName}</p>
                        <p className={`text-xs truncate mt-1 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{user?.email}</p>
                      </div>
                      <Link
                        to="/profile"
                        className={`flex items-center px-4 py-3 text-sm transition-all duration-200 group relative ${dark ? 'text-gray-300 hover:bg-red-600/20 hover:text-white' : 'text-gray-600 hover:bg-red-50 hover:text-gray-900'}`}
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center mr-3 group-hover:bg-red-600/20 transition-colors">
                          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <span>My Profile</span>
                        <svg className="w-4 h-4 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                      <button
                        onClick={() => { setIsProfileOpen(false); handleLogout(); }}
                        className={`flex items-center w-full text-left px-4 py-3 text-sm transition-all duration-200 group relative ${dark ? 'text-gray-300 hover:bg-red-600/20 hover:text-white' : 'text-gray-600 hover:bg-red-50 hover:text-gray-900'}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-red-600/10 flex items-center justify-center mr-3 group-hover:bg-red-600/20 transition-colors">
                          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                        </div>
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className={`relative px-5 py-2 rounded-lg text-sm font-medium transition-all duration-300 group ${linkClass}`}>
                  <span className="relative z-10">Login</span>
                  <span className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-600/20 to-red-600/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" />
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-red-600 group-hover:w-3/4 transition-all duration-300" />
                </Link>
                <Link
                  to="/register"
                  className="relative bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 shadow-lg shadow-red-600/30 hover:shadow-red-600/50 hover:scale-105 ml-2 overflow-hidden group border border-red-500/50"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Register
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </Link>
              </>
            )}

            {/* ✅ Theme Toggle in Navbar */}
            <button
              onClick={toggleTheme}
              className={`ml-3 w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 border ${dark ? 'bg-white/5 border-white/10 text-yellow-300 hover:bg-white/10' : 'bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200'}`}
              title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {dark
                ? <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd"/></svg>
                : <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/></svg>
              }
            </button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            {/* Theme toggle on mobile too */}
            <button
              onClick={toggleTheme}
              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 border ${dark ? 'bg-white/5 border-white/10 text-yellow-300' : 'bg-gray-100 border-gray-200 text-gray-700'}`}
              title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {dark
                ? <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd"/></svg>
                : <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/></svg>
              }
            </button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-lg transition-all duration-300 border ${dark ? 'text-gray-300 hover:text-white hover:bg-red-600/20 border-transparent hover:border-red-600/30' : 'text-gray-600 hover:text-gray-900 hover:bg-red-50 border-transparent hover:border-red-200'}`}
            >
              <svg
                className={`h-6 w-6 transition-transform duration-300 ${isMenuOpen ? 'rotate-90' : ''}`}
                fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                viewBox="0 0 24 24" stroke="currentColor"
              >
                {isMenuOpen
                  ? <path d="M6 18L18 6M6 6l12 12" />
                  : <path d="M4 6h16M4 12h16M4 18h16" />
                }
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className={`md:hidden border-t animate-slideDown ${dark ? 'border-red-600/20' : 'border-gray-100'}`}>
          <div className={`px-4 pt-2 pb-4 space-y-2 backdrop-blur-xl ${dark ? 'bg-black/98' : 'bg-white/98'}`}>

            {isAuthenticated() && (
              <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border mb-3 ${dark ? 'bg-gradient-to-r from-red-600/10 to-transparent border-red-600/20' : 'bg-red-50 border-red-100'}`}>
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-red-700 flex items-center justify-center text-white font-semibold shadow-lg">
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-black" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-bold truncate ${dark ? 'text-white' : 'text-gray-900'}`}>{user?.firstName} {user?.lastName}</p>
                  <p className={`text-xs truncate ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{user?.email}</p>
                </div>
              </div>
            )}

            {[
              { to: '/', label: 'Home', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
              { to: '/contests', label: 'Contests', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
            ].map(({ to, label, icon }) => (
              <Link key={to} to={to}
                className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 border-l-4 border-transparent ${dark ? 'text-gray-300 hover:text-white hover:bg-red-600/20 hover:border-red-600' : 'text-gray-600 hover:text-gray-900 hover:bg-red-50 hover:border-red-500'}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <svg className="w-5 h-5 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={icon} />
                </svg>
                {label}
              </Link>
            ))}

            {isAuthenticated() ? (
              <>
                {isAdmin && (
                  <Link to="/admin/dashboard"
                    className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 border-l-4 border-transparent ${dark ? 'text-gray-300 hover:text-white hover:bg-red-600/20 hover:border-red-600' : 'text-gray-600 hover:text-gray-900 hover:bg-red-50 hover:border-red-500'}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg className="w-5 h-5 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Dashboard
                  </Link>
                )}
                <Link to="/profile"
                  className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 border-l-4 border-transparent ${dark ? 'text-gray-300 hover:text-white hover:bg-red-600/20 hover:border-red-600' : 'text-gray-600 hover:text-gray-900 hover:bg-red-50 hover:border-red-500'}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className="w-5 h-5 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Dashboard
                </Link>
                <button
                  onClick={() => { setIsMenuOpen(false); handleLogout(); }}
                  className={`flex items-center w-full text-left px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 border-l-4 border-transparent ${dark ? 'text-gray-300 hover:text-white hover:bg-red-600/20 hover:border-red-600' : 'text-gray-600 hover:text-gray-900 hover:bg-red-50 hover:border-red-500'}`}
                >
                  <svg className="w-5 h-5 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login"
                  className={`flex items-center px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 border-l-4 border-transparent ${dark ? 'text-gray-300 hover:text-white hover:bg-red-600/20 hover:border-red-600' : 'text-gray-600 hover:text-gray-900 hover:bg-red-50 hover:border-red-500'}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className="w-5 h-5 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                  Login
                </Link>
                <Link to="/register"
                  className="flex items-center justify-center bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 px-4 py-3 rounded-lg text-base font-semibold transition-all duration-300 shadow-lg shadow-red-600/30 hover:scale-105 border border-red-500/50 mt-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  Register Now
                </Link>
              </>
            )}
          </div>
        </div>
      )}

      {/* ✅ Marquee ticker strip — sits at the very bottom of the navbar */}
      <div className={`border-t overflow-hidden ${dark ? 'border-red-600/15 bg-red-500/5' : 'border-red-100 bg-red-50/80'}`}>
        <div className="marquee-ticker py-1.5">
          <div className="marquee-ticker-inner">
            {[...Array(2)].map((_, ri) => (
              <div key={ri} className="flex items-center gap-8 px-8">
                {[
                  // 'WE ARE PERFORMING MAINTENANCE ON OUR PAYMENT SYSTEM. PAYSTACK PAYMENTS ARE TEMPORARILY DISABLED. PLEASE USE USSD FOR PAYMENTS IN THE MEANTIME.',
                  // 'WE ARE PERFORMING MAINTENANCE ON OUR PAYMENT SYSTEM. PAYSTACK PAYMENTS ARE TEMPORARILY DISABLED. PLEASE USE USSD FOR PAYMENTS IN THE MEANTIME.',
                  // 'WE ARE PERFORMING MAINTENANCE ON OUR PAYMENT SYSTEM. PAYSTACK PAYMENTS ARE TEMPORARILY DISABLED. PLEASE USE USSD FOR PAYMENTS IN THE MEANTIME.',
                  'Paystack Payments',
                  'MoMo Support',
                  'SMS Alerts',
                  'Mobile First',
                ].map((t, i) => (
                  <span
                    key={i}
                    className={`flex items-center gap-2 text-[11px] font-bold whitespace-nowrap tracking-widest uppercase ${dark ? 'text-red-400/70' : 'text-red-500/80'}`}
                  >
                    <span className="w-1 h-1 rounded-full bg-current inline-block" />
                    {t}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes dropdown {
          from { opacity: 0; transform: translateY(-10px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes ticker {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .animate-slideDown  { animation: slideDown 0.3s ease-out; }
        .animate-dropdown   { animation: dropdown 0.2s ease-out; }
        .marquee-ticker { overflow: hidden; }
        .marquee-ticker-inner {
          display: flex;
          width: max-content;
          animation: ticker 28s linear infinite;
        }
        .marquee-ticker-inner:hover { animation-play-state: paused; }
      `}</style>
    </nav>
  );
};

export default Navbar;